import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNewTransactionStore } from "@repo/core/stores/newTransaction";
import { useNewTransactionFlow } from "@repo/core/hooks/useNewTransactionFlow";
import { InterestTypeEnum } from "@repo/core/enums/installments";
import { InstallmentSimulateOperationTypeEnum } from "@repo/core/enums/installments";
import { AuthorizerResource } from "@repo/core/services/resources/authorizer";
import { apiInstance } from "@repo/core/services/instances/api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { installmentsFormSchema } from "@repo/core/helpers/zod.schemas";
import { z } from "zod";
import { ROUTES } from "@repo/core/routes";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { useNavigation } from "@repo/core/hooks/useNavigation";

const authorizerResource = new AuthorizerResource(apiInstance);

export const NewTransactionInstallmentsController = () => {
  // Hooks
  const { navigateBack, currentRouteConfig, customNavigate } =
    useNewTransactionFlow();
  const { replace } = useNavigation();
  const { isMobile } = useDimensions();

  // Store
  const {
    setSelectedInstallment,
    setInterestType,
    selectedProduct,
    transactionValue,
    qrCodeInfo,
    resetNewTransaction
  } = useNewTransactionStore();

  // Form
  const { control } = useForm<z.infer<typeof installmentsFormSchema>>({
    resolver: zodResolver(installmentsFormSchema),
    defaultValues: {
      purchaseValue: transactionValue.toString()
    }
  });

  // Local State
  const [selectedInterestType, setSelectedInterestType] = useState(
    qrCodeInfo?.strSemJuros || InterestTypeEnum.WITH_INTEREST
  );
  const [selectedInstallmentId, setSelectedInstallmentId] = useState<
    number | null
  >(null);

  // Query
  const { data: installmentOptions, isFetching } = useQuery({
    queryKey: [
      "consultPlanTerms",
      qrCodeInfo?.strSemJuros,
      selectedInterestType,
      selectedProduct?.strProduto,
      selectedProduct?.lngCartao,
      transactionValue
    ],
    queryFn: async () => {
      const response = await authorizerResource.consultPlanTerms({
        strProduto: selectedProduct?.strProduto || "",
        lngCartao: selectedProduct?.lngCartao.toString() || "",
        strCodigoEstabelecimento: "000000000000593",
        strTipoOperacao: InstallmentSimulateOperationTypeEnum.PURCHASE,
        dblValorOperacao: transactionValue,
        strSemJuros: selectedInterestType
      });
      return response.data;
    },
    enabled: !!selectedProduct?.strProduto && !!transactionValue
  });

  // Handlers

  const handleInterestTypeSelect = (type: InterestTypeEnum) => {
    setSelectedInterestType(type);
    setInterestType(type);
    setSelectedInstallmentId(null);
  };

  const handleInstallmentSelect = (id: number) => {
    setSelectedInstallmentId(id);
  };

  const handleSubmitInstallments = async () => {
    if (selectedInstallmentId === null) return;

    const selectedInstallmentOption =
      selectedInterestType === InterestTypeEnum.WITH_INTEREST
        ? installmentOptions?.[0]?.lstPrazos?.[selectedInstallmentId]
        : installmentOptions?.[1]?.lstPrazos?.[selectedInstallmentId];

    if (!selectedInstallmentOption) return;

    setSelectedInstallment(selectedInstallmentOption);
    setInterestType(selectedInterestType);

    customNavigate({
      to: ROUTES.newTransactionReview,
      previous: ROUTES.newTransactionInstallments
    });
  };
  const handleOnCancel = () => {
    resetNewTransaction();
    replace(ROUTES.sales);
  };

  useEffect(() => {
    if (qrCodeInfo?.strPrazo && installmentOptions) {
      const installmentId = installmentOptions?.[0]?.lstPrazos?.findIndex(
        (installment) => installment.intPrazo === Number(qrCodeInfo.strPrazo)
      );

      setSelectedInstallmentId(installmentId || null);
    }
  }, [qrCodeInfo, installmentOptions]);

  return {
    // Layout
    isMobile,

    // Form
    control,

    // Data
    selectedInterestType,
    installmentOptions:
      selectedInterestType === InterestTypeEnum.WITH_INTEREST
        ? installmentOptions?.[0]?.lstPrazos
        : installmentOptions?.[1]?.lstPrazos,
    selectedInstallmentId,
    isFetching,
    title: currentRouteConfig.title,
    // Handlers
    handleBack: navigateBack,
    handleInterestTypeChange: handleInterestTypeSelect,
    handleInstallmentChange: handleInstallmentSelect,
    handleOnSubmitForm: handleSubmitInstallments,
    handleOnCancel
  };
};
